/**
 * The root .gitattributes of a generated project: the generated STATE files are
 * declared `merge=binary`. Two branches that both ran a generator both rewrote
 * those files; a line merge of them is meaningless, and a hand merge is how they
 * get corrupted. With `merge=binary`, a conflict keeps the current branch's
 * version WITHOUT markers (the JSON stays valid) and flags the path unmerged —
 * `/gitflow update` and `/gitflow sync` then resolve it (gitflow
 * lib/state-conflicts: dev-state and ledger by union, core-seed state by
 * regeneration), and audit-lifecycle LIFE-017 catches what slipped through.
 *
 * `ss init` writes ROOT_GITATTRIBUTES; `ss upgrade` appends the missing lines to
 * an existing file (planGitattributes — idempotent, never rewrites a line).
 * ModelSnapshot.cs is NOT listed: it is C#, a text conflict there is resolved by
 * `/efcore rebase-snapshot`.
 */

export const GENERATED_STATE_GITATTRIBUTES_HEADER =
  '# SmartStack generated state — never merged line by line: on a conflict git keeps the current branch version\n' +
  '# (no markers) and flags the file; /gitflow update and /gitflow sync resolve it. Never resolve these by hand.';

export const GENERATED_STATE_GITATTRIBUTES: readonly string[] = [
  '.smartstack/dev-state/** merge=binary',
  '.smartstack/core-seed/*.state.json merge=binary',
  '.smartstack/generated-ledger.json merge=binary',
];

export const ROOT_GITATTRIBUTES = `${GENERATED_STATE_GITATTRIBUTES_HEADER}\n${GENERATED_STATE_GITATTRIBUTES.join('\n')}\n`;

/** `pattern attr attr…` of a non-comment line, or null. */
function parseLine(line: string): { pattern: string; attrs: string[] } | null {
  const t = line.trim();
  if (!t || t.startsWith('#')) return null;
  const [pattern, ...attrs] = t.split(/\s+/);
  return { pattern, attrs };
}

/** True when an attribute token sets, unsets or unspecifies `merge` (merge, -merge, !merge, merge=…). */
function touchesMerge(attr: string): boolean {
  return /^[-!]?merge(=|$)/.test(attr);
}

/**
 * The content of `.gitattributes` with every missing generated-state line
 * appended, or null when nothing is missing. A pattern already carrying ANY
 * `merge` attribute is the project's own decision and is left alone. Keeps the
 * file's line endings.
 */
export function planGitattributes(existing: string | null): { content: string; added: string[] } | null {
  if (existing === null || existing.trim() === '') return { content: ROOT_GITATTRIBUTES, added: [...GENERATED_STATE_GITATTRIBUTES] };
  const decided = new Set<string>();
  for (const line of existing.split(/\r?\n/)) {
    const parsed = parseLine(line);
    if (parsed && parsed.attrs.some(touchesMerge)) decided.add(parsed.pattern);
  }
  const added = GENERATED_STATE_GITATTRIBUTES.filter((line) => !decided.has(line.split(/\s+/)[0]));
  if (added.length === 0) return null;
  const eol = existing.includes('\r\n') ? '\r\n' : '\n';
  const head = existing.endsWith('\n') ? existing : existing + eol;
  const headerLines = GENERATED_STATE_GITATTRIBUTES_HEADER.split('\n');
  const block = [...(existing.includes(headerLines[0]) ? [] : headerLines), ...added];
  const separator = /(\r?\n){2}$/.test(head) ? '' : eol; // one blank line before the block
  return { content: `${head}${separator}${block.join(eol)}${eol}`, added };
}
